import React, { useEffect, useState } from 'react'
import { ButtonType, CustomButton, InputWrapper } from '../../components'
import { toMenuOptions, WemoloDropdown, WemoloInput } from '../../components/Wemolo'
import styles from './ODDisplayOverride.module.css'

type SupportedDisplayModes = 'normal' | 'overwrite_number' | 'overwrite_text' | 'disable' | 'clock'

type DisplayModeConfig = { mode: 'normal' | 'disable' | 'clock'; value: null } | { mode: 'overwrite'; value: string }

export type ODDisplayOverrideConfig = {
    display_mode: DisplayModeConfig
} | null

const getInitialMode = (config: ODDisplayOverrideConfig): SupportedDisplayModes => {
    if (!config || !config.display_mode) {
        return 'normal'
    }
    const { mode, value } = config.display_mode
    if (mode === 'overwrite') {
        return typeof value === 'string' && (value === '' || isNaN(Number(value)))
            ? 'overwrite_text'
            : 'overwrite_number'
    }
    return mode as SupportedDisplayModes
}

export const ODDisplayOverride = ({
    config,
    saveConfig,
    loading
}: {
    config: ODDisplayOverrideConfig
    saveConfig: (config: ODDisplayOverrideConfig) => void
    loading: boolean
}) => {
    const [selectedMode, setSelectedMode] = useState<SupportedDisplayModes>(getInitialMode(config))
    const [value, setValue] = useState<string | number | null>(config?.display_mode?.value || null)
    const [error, setError] = useState(false)

    useEffect(() => {
        if (selectedMode === 'overwrite_number' && value && (value.toString().length > 4 || Number(value) < 0)) {
            setError(true)
        } else {
            setError(false)
        }
    }, [value, selectedMode])

    useEffect(() => {
        setSelectedMode(getInitialMode(config))
        setValue(config?.display_mode?.value || null)
    }, [config])

    const displayModeOptions = [
        { label: 'Normal', id: 'normal' },
        { label: 'Overwrite (number)', id: 'overwrite_number' },
        { label: 'Overwrite (text)', id: 'overwrite_text' },
        { label: 'Disable', id: 'disable' },
        { label: 'Clock', id: 'clock' }
    ]

    const textOverwriteOptions = [
        { label: 'FREI', id: 'FREI' },
        { label: 'VOLL', id: 'VOLL' },
        { label: 'TEST', id: 'TEST' }
    ]

    const shouldShowNumberField = selectedMode === 'overwrite_number'
    const shouldShowTextField = selectedMode === 'overwrite_text'

    const handleModeChange = (id: string | undefined) => {
        setSelectedMode((id as SupportedDisplayModes) || 'normal')
        setValue('')
    }

    const handleSave = () => {
        let configValue: DisplayModeConfig

        if (selectedMode === 'overwrite_number') {
            configValue = { mode: 'overwrite', value: String(value ?? '') }
        } else if (selectedMode === 'overwrite_text') {
            configValue = { mode: 'overwrite', value: String(value ?? '') }
        } else {
            configValue = {
                mode: selectedMode as 'normal' | 'disable' | 'clock',
                value: null
            }
        }
        saveConfig({
            display_mode: configValue
        })
    }
    return (
        <div className={styles.root}>
            <div className={styles.dropdownCol}>
                <InputWrapper label="Display Mode">
                    <WemoloDropdown
                        size="medium"
                        options={toMenuOptions(displayModeOptions)}
                        value={selectedMode ?? undefined}
                        onChange={(id) => handleModeChange(id)}
                    />
                </InputWrapper>
            </div>

            {shouldShowNumberField && (
                <div className={styles.dropdownCol}>
                    <InputWrapper label="Value">
                        <WemoloInput
                            type="number"
                            value={value || ''}
                            onChange={(e) => setValue(e.target.value)}
                            error={error}
                        />
                    </InputWrapper>
                </div>
            )}
            {shouldShowTextField && (
                <div className={styles.dropdownCol}>
                    <InputWrapper label="Value">
                        <WemoloDropdown
                            size="medium"
                            options={toMenuOptions(textOverwriteOptions)}
                            value={value ? String(value) : 'TEST'}
                            onChange={(id) => setValue(id || '')}
                        />
                    </InputWrapper>
                </div>
            )}
            <div className={styles.buttonCol}>
                <CustomButton
                    isLoading={loading}
                    onClick={handleSave}
                    type={ButtonType.SAVE}
                    extraProps={{ minWidth: '270px !important;' }}
                    disabled={error}
                    size="medium"
                >
                    Save Display Config
                </CustomButton>
            </div>
        </div>
    )
}
